// src/utils/format/relative-time.ts

/**
 * Locale-aware wrappers around date-fns relative-time formatting.
 *
 * Components should use these helpers instead of calling
 * `formatDistanceToNow` / `formatDistance` directly so that user-visible
 * relative times ("2 hours ago", "in 7 days") follow the active UI locale
 * instead of always rendering in English.
 *
 * The current locale is read from the global vue-i18n composer on every
 * call. Because `globalComposer.locale` is a reactive ref, any `computed()`
 * (or render function) that calls these helpers is re-evaluated when the
 * user switches language — no extra watchers needed.
 */

import { globalComposer } from '@/i18n';
import {
  formatDistance,
  formatDistanceToNow,
  type FormatDistanceOptions,
  type FormatDistanceToNowOptions,
} from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import type { Locale } from 'date-fns';

/**
 * Static map from the app's locale codes (normalized to lowercase with
 * underscore separators) to date-fns Locale objects.
 *
 * Only the locales the app actually supports today (en, pt_BR) are
 * imported, keeping the full date-fns/locale catalog out of the bundle.
 * When a new app locale is enabled, add its date-fns locale here.
 */
const DATE_FNS_LOCALES: Record<string, Locale> = {
  en: enUS,
  pt: ptBR,
  pt_br: ptBR,
};

const DEFAULT_DATE_FNS_LOCALE = enUS;

/**
 * Resolve the date-fns Locale matching the active vue-i18n locale.
 *
 * Falls back from the exact code (pt_BR) to the primary language (pt),
 * then to English.
 */
export function currentDateFnsLocale(): Locale {
  const code = String(globalComposer.locale.value ?? '')
    .toLowerCase()
    .replace('-', '_');
  return DATE_FNS_LOCALES[code] ?? DATE_FNS_LOCALES[code.split('_')[0]] ?? DEFAULT_DATE_FNS_LOCALE;
}

/**
 * Locale-aware `formatDistanceToNow`.
 *
 * @example formatRelativeTimeToNow(date, { addSuffix: true }) // "2 hours ago" / "há 2 horas"
 */
export const formatRelativeTimeToNow = (
  date: Date | number,
  options?: FormatDistanceToNowOptions
): string => formatDistanceToNow(date, { ...options, locale: currentDateFnsLocale() });

/**
 * Locale-aware `formatDistance` between two dates.
 *
 * @example formatRelativeDistance(end, now, { addSuffix: true }) // "in 7 days" / "em 7 dias"
 */
export const formatRelativeDistance = (
  laterDate: Date | number,
  earlierDate: Date | number,
  options?: FormatDistanceOptions
): string => formatDistance(laterDate, earlierDate, { ...options, locale: currentDateFnsLocale() });
